import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Image, ImagePlus, TrendingUp, ChevronRight, Settings,
  LogOut, AlertTriangle, X, Lock, Eye, EyeOff,
  CheckCircle2, AlertCircle, ArrowRightLeft, Phone,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { authAPI } from '../../api/auth';
import PageHeader from '../../components/ui/PageHeader';
import Button from '../../components/ui/Button';

const SETTINGS_ITEMS = [
  {
    icon: Image,
    label: 'Banner Manager',
    description: 'Add, view and delete promotional banners',
    to: '/banners',
  },
  {
    icon: ImagePlus,
    label: 'Logo Manager',
    description: 'Upload or replace the application logo',
    to: '/logo',
  },
  {
    icon: TrendingUp,
    label: 'Gold Rate',
    description: 'Update the current gold rate shown to users',
    to: '/gold-rate',
  },
  {
    icon: ArrowRightLeft,
    label: 'Withdrawal Transfers',
    description: 'Track and update fund transfer status for approved withdrawals',
    to: '/withdrawal-transfers',
  },
  {
    icon: Phone,
    label: 'Contact Info',
    description: 'Update phone, email, address and social links shown to users',
    to: '/contact-info',
  },
];

// ── Password field with show/hide toggle ─────────────────────
function PasswordInput({ label, name, value, onChange, error, placeholder }) {
  const [show, setShow] = useState(false);
  return (
    <div className="flex flex-col gap-1.5">
      <label className="input-label">{label}</label>
      <div className="relative">
        <input
          type={show ? 'text' : 'password'}
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          autoComplete="off"
          className={`input-field pr-10 ${error ? 'border-red-400' : ''}`}
        />
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          className="absolute right-3 top-1/2 -translate-y-1/2"
          tabIndex={-1}
        >
          {show
            ? <EyeOff size={15} style={{ color: 'rgba(255,255,255,0.35)' }} />
            : <Eye    size={15} style={{ color: 'rgba(255,255,255,0.35)' }} />}
        </button>
      </div>
      {error && <p className="text-xs" style={{ color: '#fca5a5' }}>{error}</p>}
    </div>
  );
}

// ── Change Password Sheet ─────────────────────────────────────
function ChangePasswordSheet({ onClose }) {
  const [form, setForm] = useState({
    current_password: '',
    new_password: '',
    new_password_confirmation: '',
  });
  const [errors,    setErrors]    = useState({});
  const [apiError,  setApiError]  = useState('');
  const [success,   setSuccess]   = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: '' }));
    if (apiError)     setApiError('');
  };

  const validate = () => {
    const errs = {};
    if (!form.current_password.trim())
      errs.current_password = 'Current password is required';
    if (!form.new_password)
      errs.new_password = 'New password is required';
    else if (form.new_password.length < 8)
      errs.new_password = 'Password must be at least 8 characters';
    if (!form.new_password_confirmation)
      errs.new_password_confirmation = 'Please confirm your new password';
    else if (form.new_password !== form.new_password_confirmation)
      errs.new_password_confirmation = 'Passwords do not match';
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setSubmitting(true);
    setApiError('');
    try {
      const { data } = await authAPI.changePassword({
        current_password:          form.current_password,
        new_password:              form.new_password,
        new_password_confirmation: form.new_password_confirmation,
      });
      if (data.status) {
        setSuccess(true);
      } else {
        setApiError(data.message || 'Failed to change password.');
      }
    } catch (err) {
      setApiError(
        err?.response?.data?.message ||
        err?.response?.data?.errors?.current_password?.[0] ||
        err?.response?.data?.errors?.new_password?.[0] ||
        'Something went wrong. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-end justify-center"
      style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)' }}
      onClick={() => !submitting && onClose()}
    >
      <div
        className="w-full max-w-app animate-fade-up rounded-t-3xl p-6 pb-8"
        style={{
          background: 'linear-gradient(180deg,#0e2060 0%,#060f38 100%)',
          border: '1px solid rgba(212,160,23,0.2)',
          borderBottom: 'none',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="mb-5 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl flex-shrink-0"
                 style={{ background: 'rgba(212,160,23,0.12)', border: '1px solid rgba(212,160,23,0.2)' }}>
              <Lock size={16} style={{ color: '#d4a017' }} />
            </div>
            <div>
              <h3 className="font-serif text-base font-semibold text-white">Change Password</h3>
              <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.4)' }}>
                Update your admin account password
              </p>
            </div>
          </div>
          {!submitting && (
            <button
              onClick={onClose}
              className="flex h-7 w-7 items-center justify-center rounded-xl"
              style={{ background: 'rgba(255,255,255,0.08)' }}
            >
              <X size={14} className="text-white" />
            </button>
          )}
        </div>

        {/* Success state */}
        {success ? (
          <div className="flex flex-col items-center gap-4 py-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-full"
                 style={{ background: 'rgba(5,150,105,0.15)', border: '1px solid rgba(5,150,105,0.3)' }}>
              <CheckCircle2 size={28} style={{ color: '#6ee7b7' }} />
            </div>
            <p className="text-sm font-semibold text-white">Password changed successfully</p>
            <button
              onClick={onClose}
              className="w-full rounded-2xl py-3.5 text-sm font-semibold transition-all active:scale-95"
              style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.7)' }}
            >
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">

            {/* API error */}
            {apiError && (
              <div className="flex items-start gap-2 rounded-xl px-3 py-2.5 text-xs"
                   style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', color: '#fca5a5' }}>
                <AlertCircle size={13} className="mt-0.5 flex-shrink-0" />
                <span>{apiError}</span>
              </div>
            )}

            <PasswordInput
              label="Current Password"
              name="current_password"
              value={form.current_password}
              onChange={handleChange}
              error={errors.current_password}
              placeholder="Enter current password"
            />
            <PasswordInput
              label="New Password"
              name="new_password"
              value={form.new_password}
              onChange={handleChange}
              error={errors.new_password}
              placeholder="Min. 8 characters"
            />
            <PasswordInput
              label="Confirm New Password"
              name="new_password_confirmation"
              value={form.new_password_confirmation}
              onChange={handleChange}
              error={errors.new_password_confirmation}
              placeholder="Re-enter new password"
            />

            <div className="flex gap-3 pt-1">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="flex-1 rounded-2xl py-3.5 text-sm font-semibold transition-all active:scale-95 disabled:opacity-50"
                style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.7)' }}
              >
                Cancel
              </button>
              <Button type="submit" variant="gold" loading={submitting} className="flex-1">
                Update Password
              </Button>
            </div>

          </form>
        )}
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────
export default function SettingsPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const [confirming,        setConfirming]        = useState(false);
  const [loggingOut,        setLoggingOut]        = useState(false);
  const [changingPassword,  setChangingPassword]  = useState(false);

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      await logout();
    } finally {
      setLoggingOut(false);
      setConfirming(false);
    }
  };

  return (
    <>
      <div className="flex flex-col min-h-0">
        <PageHeader title="Settings" />

        <div className="flex flex-col gap-4 p-4">

          {/* ── Manage section ── */}
          <div className="flex items-center gap-2 px-1">
            <Settings size={13} style={{ color: 'rgba(212,160,23,0.7)' }} />
            <p className="text-xs font-semibold uppercase tracking-widest"
               style={{ color: 'rgba(255,255,255,0.35)' }}>
              Manage
            </p>
          </div>

          <div className="flex flex-col gap-2">
            {SETTINGS_ITEMS.map(({ icon: Icon, label, description, to }) => (
              <button
                key={to}
                onClick={() => navigate(to)}
                className="card flex items-center gap-4 text-left transition-all duration-200 active:scale-[0.98]"
                style={{ cursor: 'pointer' }}
              >
                <div
                  className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-2xl"
                  style={{ background: 'rgba(212,160,23,0.12)', border: '1px solid rgba(212,160,23,0.2)' }}
                >
                  <Icon size={18} style={{ color: '#d4a017' }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-white">{label}</p>
                  <p className="text-xs mt-0.5 truncate" style={{ color: 'rgba(255,255,255,0.4)' }}>
                    {description}
                  </p>
                </div>
                <ChevronRight size={16} style={{ color: 'rgba(255,255,255,0.25)' }} className="flex-shrink-0" />
              </button>
            ))}
          </div>

          {/* ── Account section ── */}
          <div className="flex items-center gap-2 px-1 mt-2">
            <LogOut size={13} style={{ color: 'rgba(239,68,68,0.7)' }} />
            <p className="text-xs font-semibold uppercase tracking-widest"
               style={{ color: 'rgba(255,255,255,0.35)' }}>
              Account
            </p>
          </div>

          {/* Admin info */}
          {user?.name && (
            <div className="card flex items-center gap-3">
              <div
                className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-2xl font-semibold text-sm"
                style={{ background: 'rgba(212,160,23,0.12)', border: '1px solid rgba(212,160,23,0.2)', color: '#d4a017' }}
              >
                {user.name.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-white truncate">{user.name}</p>
                {user.email && (
                  <p className="text-xs mt-0.5 truncate" style={{ color: 'rgba(255,255,255,0.4)' }}>
                    {user.email}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Change Password */}
          <button
            onClick={() => setChangingPassword(true)}
            className="card flex items-center gap-4 text-left transition-all duration-200 active:scale-[0.98]"
            style={{ cursor: 'pointer' }}
          >
            <div
              className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-2xl"
              style={{ background: 'rgba(212,160,23,0.12)', border: '1px solid rgba(212,160,23,0.2)' }}
            >
              <Lock size={18} style={{ color: '#d4a017' }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-white">Change Password</p>
              <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.4)' }}>
                Update your admin account password
              </p>
            </div>
            <ChevronRight size={16} style={{ color: 'rgba(255,255,255,0.25)' }} className="flex-shrink-0" />
          </button>

          {/* Sign Out */}
          <button
            onClick={() => setConfirming(true)}
            className="card flex items-center gap-4 text-left transition-all duration-200 active:scale-[0.98]"
            style={{ cursor: 'pointer' }}
          >
            <div
              className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-2xl"
              style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.25)' }}
            >
              <LogOut size={18} style={{ color: '#fca5a5' }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold" style={{ color: '#fca5a5' }}>Sign Out</p>
              <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.4)' }}>
                You will be returned to the login screen
              </p>
            </div>
            <ChevronRight size={16} style={{ color: 'rgba(255,255,255,0.25)' }} className="flex-shrink-0" />
          </button>

        </div>
      </div>

      {/* ── Change Password Sheet ── */}
      {changingPassword && (
        <ChangePasswordSheet onClose={() => setChangingPassword(false)} />
      )}

      {/* ── Logout confirmation sheet ── */}
      {confirming && (
        <div
          className="fixed inset-0 z-[200] flex items-end justify-center"
          style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)' }}
          onClick={() => !loggingOut && setConfirming(false)}
        >
          <div
            className="w-full max-w-app animate-fade-up rounded-t-3xl p-6"
            style={{
              background: 'linear-gradient(180deg, #0e2060 0%, #060f38 100%)',
              border: '1px solid rgba(212,160,23,0.2)',
              borderBottom: 'none',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-5 flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-2xl flex-shrink-0"
                     style={{ background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)' }}>
                  <AlertTriangle size={18} style={{ color: '#fca5a5' }} />
                </div>
                <div>
                  <h3 className="font-serif text-base font-semibold text-white">Sign Out</h3>
                  <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.45)' }}>
                    You will be returned to the login screen
                  </p>
                </div>
              </div>
              {!loggingOut && (
                <button
                  onClick={() => setConfirming(false)}
                  className="flex h-7 w-7 items-center justify-center rounded-xl transition-opacity hover:opacity-70"
                  style={{ background: 'rgba(255,255,255,0.08)' }}
                >
                  <X size={14} className="text-white" />
                </button>
              )}
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setConfirming(false)}
                disabled={loggingOut}
                className="flex-1 rounded-2xl py-3.5 text-sm font-semibold transition-all duration-200 active:scale-95 disabled:opacity-50"
                style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.7)' }}
              >
                Cancel
              </button>
              <button
                onClick={handleLogout}
                disabled={loggingOut}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-2xl py-3.5 text-sm font-semibold transition-all duration-200 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
                style={{ background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)', boxShadow: '0 4px 16px rgba(220,38,38,0.35)', color: '#ffffff' }}
              >
                {loggingOut ? (
                  <span className="inline-block h-4 w-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                ) : (
                  <><LogOut size={15} /> Sign Out</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
